import { useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  IconButton,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { resizeImage } from '@troverecommerce/saas-components-ts';
import { isVideoUrl } from './mediaUtils';

interface MediaAssetCardProps {
  imageKey: string;
  url: string;
  source?: 'catalog' | 'studio' | 'video' | 'inventory';
  onDelete?: () => void;
  onEdit?: () => void;
  disabled?: boolean;
}

export const MediaAssetCard = ({
  imageKey,
  url,
  source,
  onDelete,
  onEdit,
  disabled = false,
}: MediaAssetCardProps) => {
  const [imageError, setImageError] = useState(false);
  const isVideo = source === 'video' || isVideoUrl(url);
  const displayUrl = url.startsWith('blob:') || isVideo ? url : resizeImage(url, 800);

  const handleDownload = async () => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = imageKey;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  return (
    <Card
      sx={{
        position: 'relative',
        width: 200,
        height: 220,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Source Badge */}
      {source && (
        <Chip
          label={source.toUpperCase()}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 1,
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.65rem',
            bgcolor: source === 'catalog' ? 'primary.main'
              : source === 'studio' ? 'secondary.dark'
              : source === 'video' ? 'success.main'
              : 'grey.600',
          }}
        />
      )}

      {/* Action Icons - Vertical Stack */}
      <Stack
        direction="column"
        spacing={0.5}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
        }}
      >
        {onEdit && (
          <IconButton
            size="small"
            onClick={onEdit}
            disabled={disabled}
            sx={{
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'action.hover' },
              boxShadow: 1,
            }}
            title="Edit"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={handleDownload}
          sx={{
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'action.hover' },
            boxShadow: 1,
          }}
          title="Download"
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
        {onDelete && (
          <IconButton
            size="small"
            onClick={onDelete}
            disabled={disabled}
            color="error"
            sx={{
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'error.lighter' },
              boxShadow: 1,
            }}
            title="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {/* Media Display */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
        {isVideo ? (
          /* eslint-disable-next-line jsx-a11y/media-has-caption */
          <video
            controls
            preload="metadata"
            src={displayUrl}
            style={{
              width: '100%',
              height: 150,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : imageError ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="error">
              Error loading image
            </Typography>
          </Box>
        ) : (
          <CardMedia
            component="img"
            image={displayUrl}
            alt={imageKey}
            onError={() => setImageError(true)}
            sx={{
              width: '100%',
              height: 150,
              objectFit: 'contain',
            }}
          />
        )}
      </Box>

      {/* Key Label */}
      <Box
        sx={{
          p: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {imageKey}
        </Typography>
      </Box>
    </Card>
  );
};
